import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import type { FirmResponse, CreateFirmRequest, UpdateFirmRequest } from '@taxassist/api-client';
import { useGetOpsOfficeLocations, useGetOpsTaxSoftware } from '@taxassist/api-client';
import { useAuth } from "../../contexts/AuthContext";

type SupportedTaxSoftwareConfig = {
  label: string;
  match: (name: string) => boolean;
};

const SUPPORTED_TAX_SOFTWARE: SupportedTaxSoftwareConfig[] = [
  {
    label: 'CCH',
    match: (name: string) => name.toLowerCase().includes('cch'),
  },
  {
    label: 'Lacerte',
    match: (name: string) => name.toLowerCase().includes('lacerte'),
  },
  {
    label: 'Ultratax',
    match: (name: string) => name.toLowerCase().includes('ultratax'),
  },
  {
    label: 'GoSystem',
    match: (name: string) => name.toLowerCase().includes('gosystem'),
  },
];

interface FirmDialogProps {
  open: boolean;
  onClose: () => void;
  firm?: FirmResponse | null;
  onSave: (firmData: CreateFirmRequest | UpdateFirmRequest) => Promise<void>;
  isLoading?: boolean;
}

interface FormData {
  name: string;


  isActive: boolean;
  surePrepConfig: {
    officeLocationId: number | string;
    accountNumber: string;
    taxSoftwareId: number | null;
    taxSoftwareName?: string;
  };
  metadata: Record<string, unknown>;
}

const initialFormData: FormData = {
  name: '',


  isActive: true,
  surePrepConfig: {
    officeLocationId: '',
    accountNumber: '',
    taxSoftwareId: null,
    taxSoftwareName: '',
  },
  metadata: {},
};

export function FirmDialog({
  open,
  onClose,
  firm,
  onSave,
  isLoading = false,
}: FirmDialogProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { user } = useAuth();

  const isEditing = !!firm;

  // Check if user is admin (only admins can access office locations)
  const isAdmin = user?.role === 'admin' || user?.groups?.includes('admin');

  // Fetch and cache office location list globally (once loaded, stays cached)
  const {
    data: officeLocationList,
    isLoading: isLoadingOfficeLocations,
    error: officeLocationsError,
  } = useGetOpsOfficeLocations({
    query: {
      queryKey: ['opsOfficeLocations'],
      enabled: isAdmin, // Load when admin user opens dialog
      staleTime: Infinity, // Office locations rarely change, cache indefinitely
      gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes after last use
    }
  });

  // Fetch and cache tax software list globally (once loaded, stays cached)
  const {
    data: taxSoftwareList,
    isLoading: isLoadingTaxSoftware,
    error: taxSoftwareError,
  } = useGetOpsTaxSoftware({
    query: {
      queryKey: ['opsTaxSoftware'],
      enabled: isAdmin, // Load when admin user opens dialog
      staleTime: Infinity, // Tax software list rarely changes, cache indefinitely
      gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes after last use
    },
  });

  // Load form data when firm prop changes
  useEffect(() => {
    if (firm) {
      setFormData({
        name: firm.name,


        isActive: firm.isActive,
        surePrepConfig: {
          officeLocationId: firm.surePrepConfig?.officeLocationId || '',
          accountNumber: firm.surePrepConfig?.accountNumber || '',
          taxSoftwareId: firm.surePrepConfig?.taxSoftwareId ?? null,
          taxSoftwareName: firm.surePrepConfig?.taxSoftwareName || '',
        },
        metadata: firm.metadata || {},
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setSubmitError(null);
  }, [firm, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // No need to validate firmId - it will be generated server-side

    if (!formData.name.trim()) {
      newErrors.name = 'Firm name is required';
    }

    if (!formData.surePrepConfig.officeLocationId) {
      newErrors.officeLocationId = 'SurePrep Office Location is required';
    }

    if (!formData.surePrepConfig.taxSoftwareId) {
      newErrors.taxSoftwareId = 'Tax Software is required';
    }

    if (!formData.surePrepConfig.accountNumber.trim()) {
      newErrors.accountNumber = 'Tax Software Account Number is required';
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field.startsWith('surePrepConfig.')) {
        const configField = field.replace('surePrepConfig.', '');
        return {
          ...prev,
          surePrepConfig: {
            ...prev.surePrepConfig,
            [configField]: value,
          },
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleOfficeLocationChange = (officeLocationId: number) => {
    setFormData(prev => ({
      ...prev,
      surePrepConfig: {
        ...prev.surePrepConfig,
        officeLocationId,
      },
    }));

    // Clear error when user selects an option
    setErrors(prev => ({
      ...prev,
      officeLocationId: '',
    }));
  };

  const handleTaxSoftwareChange = (taxSoftwareId: number) => {
    const selectedTaxSoftware = taxSoftwareList?.find(
      (software) => software.Id === taxSoftwareId,
    );

    setFormData(prev => ({
      ...prev,
      surePrepConfig: {
        ...prev.surePrepConfig,
        taxSoftwareId,
        taxSoftwareName: selectedTaxSoftware?.Name || '',
      },
    }));

    setErrors(prev => ({
      ...prev,
      taxSoftwareId: '',
    }));
  };

  // Build supported tax software options by fuzzy-matching SurePrep names.
  // If we can't find a specific vendor, fall back to the "Others" entry.
  const supportedTaxSoftwareOptions =
    taxSoftwareList?.length
      ? (() => {
        const othersEntry = taxSoftwareList.find((software) =>
          (software.Name || '').toLowerCase().includes('other'),
        );

        return SUPPORTED_TAX_SOFTWARE.map((config) => {
          const match = taxSoftwareList.find((software) =>
            config.match(software.Name || ''),
          );

          const selected = match || othersEntry;

          return selected
            ? {
              id: selected.Id,
              label: config.label,
              surePrepName: selected.Name,
            }
            : null;
        }).filter(
          (item): item is { id: number; label: string; surePrepName: string } => !!item,
        );
      })()
      : [];

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitError(null);

      const submitData = {
        ...formData,
        // Include firmId for edits, omit for new firms (server will generate)
        ...(isEditing && firm ? { firmId: firm.firmId } : {}),
        surePrepConfig: {
          ...formData.surePrepConfig,
          officeLocationId: Number(formData.surePrepConfig.officeLocationId),
          taxSoftwareId: formData.surePrepConfig.taxSoftwareId ?? undefined,
        },
      };

      await onSave(submitData);
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An error occurred while saving the firm');
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        {isEditing ? `Edit Firm: ${firm?.name}` : 'Create New Firm'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            {/* Firm ID will be generated automatically from firm name */}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
              <TextField
                fullWidth
                label="Firm Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isLoading}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>

            {/* SurePrep Configuration */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              SurePrep Config
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth error={!!errors.officeLocationId}>
                <InputLabel id="office-location-label">SurePrep Office Location *</InputLabel>
                <Select
                  labelId="office-location-label"
                  value={formData.surePrepConfig.officeLocationId || ''}
                  label="SurePrep Office Location *"
                  onChange={(e) => handleOfficeLocationChange(Number(e.target.value))}
                  disabled={isLoading || isLoadingOfficeLocations || !officeLocationList}
                >
                  <MenuItem value="">
                    <em>Select SurePrep Office Location</em>
                  </MenuItem>
                  {officeLocationList?.map((location) => (
                    <MenuItem key={location.Id} value={location.Id}>
                      {location.Name} (ID: {location.Id})
                    </MenuItem>
                  ))}
                </Select>
                {(errors.officeLocationId || officeLocationsError) && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.officeLocationId || 'Error loading office locations'}
                  </Typography>
                )}
                {!officeLocationsError && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                    Add in SurePrep if this firm and refresh if firm location does not appear
                  </Typography>
                )}
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <FormControl fullWidth error={!!errors.taxSoftwareId}>
                  <InputLabel id="tax-software-label">Tax Software *</InputLabel>
                  <Select
                    labelId="tax-software-label"
                    value={formData.surePrepConfig.taxSoftwareId ?? ''}
                    label="Tax Software *"
                    onChange={(e) => handleTaxSoftwareChange(Number(e.target.value))}
                    disabled={isLoading || isLoadingTaxSoftware || !supportedTaxSoftwareOptions.length}
                  >
                    <MenuItem value="">
                      <em>Select Tax Software</em>
                    </MenuItem>
                    {supportedTaxSoftwareOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {(errors.taxSoftwareId || taxSoftwareError) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {errors.taxSoftwareId || 'Error loading tax software list'}
                    </Typography>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  label="Tax Software Account Number *"
                  value={formData.surePrepConfig.accountNumber}
                  onChange={(e) => handleInputChange('surePrepConfig.accountNumber', e.target.value)}
                  error={!!errors.accountNumber}
                  helperText={errors.accountNumber || 'Tax software account number'}
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Saving...' : (isEditing ? 'Update Firm' : 'Create Firm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
